import React from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Divider from "@mui/material/Divider";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Chip from "@mui/material/Chip";
import DescriptionIcon from "@mui/icons-material/Description";
import SettingsIcon from "@mui/icons-material/Settings";
import VisibilityIcon from "@mui/icons-material/Visibility";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import EmailIcon from "@mui/icons-material/Email";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import DownloadIcon from "@mui/icons-material/Download";
import CloseIcon from "@mui/icons-material/Close";
import moment from "moment";
import { useLocation, Link } from "react-router";

export default function ReportHistoryModal({
  open,
  onClose,
  period,
  projectId,
  onLatestVersionClick,
  onViewReport,
  onEmailReport,
  onSendReplacement,
  onGenerateInvoiceExport,
  onDeleteReport,
  canDeleteReport = true,
}) {
  const location = useLocation();
  const returnTo = encodeURIComponent(location.pathname + location.search);
  const billingConfigUrl = `/projects/${projectId}/capture-inventory?billingConfig=billing-types&info=true&tab=settings&returnTo=${returnTo}`;
  const [menuAnchorEl, setMenuAnchorEl] = React.useState(null);
  const [selectedReport, setSelectedReport] = React.useState(null);

  const handleOpenMenu = (event, report) => {
    setMenuAnchorEl(event.currentTarget);
    setSelectedReport(report);
  };

  const handleCloseMenu = () => {
    setMenuAnchorEl(null);
    setSelectedReport(null);
  };

  const formatCurrency = amount => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(amount || 0);
  };

  const selectedReportNotificationDetails = selectedReport?.notified
    ? `Notified at ${moment(selectedReport.notifiedAt).format("MMM D, YYYY h:mm A")} by ${selectedReport.notifiedByUser?.name || selectedReport.notifiedByUser?.email || "Unknown"}`
    : null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ position: "relative" }}>
        <IconButton
          aria-label="Close report history"
          onClick={onClose}
          size="small"
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: theme => theme.palette.grey[600],
          }}
        >
          <CloseIcon />
        </IconButton>
        <Box textAlign="center" sx={{ pr: { xs: 5, sm: 6 } }}>
          <Typography
            variant="h5"
            fontWeight={500}
            color="black"
            sx={{ fontSize: { xs: 18, md: 22 } }}
          >
            {period?.label} - Report History
          </Typography>
          <Typography variant="body2" color="text.secondary" mt={0.5}>
            {period && moment(period.startDate).format("MM/DD/YYYY")} -{" "}
            {period && moment(period.endDate).format("MM/DD/YYYY")}
          </Typography>
          <Box mt={2} display="flex" justifyContent="center" gap={2}>
            <Button
              variant="outlined"
              color="primary"
              size="small"
              startIcon={<DescriptionIcon />}
              onClick={onLatestVersionClick}
            >
              Latest Version
            </Button>
            <Button
              variant="outlined"
              color="primary"
              size="small"
              startIcon={<SettingsIcon />}
              component={Link}
              to={billingConfigUrl}
            >
              Billing Config
            </Button>
          </Box>
        </Box>
      </DialogTitle>
      <DialogContent>
        {period?.billingReports?.length > 0 ? (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell align="center">Version</TableCell>
                  <TableCell align="center">Date Generated</TableCell>
                  <TableCell align="center">Submitted By</TableCell>
                  <TableCell align="center">Grand Total</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {period.billingReports.map(report => (
                  <TableRow key={report.id}>
                    <TableCell align="center">
                      <Box display="flex" flexDirection="column" alignItems="center">
                        <Chip
                          label={`Version ${report.version}`}
                          size="small"
                          sx={report.notified ? {
                            color: "success.main",
                            backgroundColor: theme => `${theme.palette.success.main}14`,
                          } : undefined}
                        />
                        {report.notified && (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            display="block"
                            mt={0.75}
                            textAlign="center"
                          >
                            Notified
                          </Typography>
                        )}
                        {report.invoice && (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            display="block"
                            mt={0.5}
                          >
                            Invoice {report.invoice.number}
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell align="center">{moment(report.createdAt).format("MMM D, YYYY h:mm A")}</TableCell>
                    <TableCell align="center">{report.user?.email || "—"}</TableCell>
                    <TableCell align="center">
                      {formatCurrency(report.billingCharges?.totals?.grand_total || 0)}
                    </TableCell>
                    <TableCell align="center">
                      <Box display="flex" gap={1} justifyContent="center">
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<VisibilityIcon />}
                          onClick={() => onViewReport(report)}
                        >
                          View
                        </Button>
                        <IconButton
                          size="small"
                          aria-label="More options"
                          onClick={event => handleOpenMenu(event, report)}
                        >
                          <MoreVertIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Box py={4} textAlign="center">
            <Typography color="text.secondary" mb={2}>
              No reports generated for this period yet
            </Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
      <Menu anchorEl={menuAnchorEl} open={Boolean(menuAnchorEl)} onClose={handleCloseMenu}>
        {selectedReport?.notified ? (
          <>
            <Box sx={{ px: 2, py: 1.5, maxWidth: 280 }}>
              <Typography variant="subtitle2" color="text.primary">
                Billing Report Notified
              </Typography>
              <Divider sx={{ my: 1 }} />
              <Typography variant="body2" color="text.secondary">
                {selectedReportNotificationDetails}
              </Typography>
            </Box>
            <Divider />
          </>
        ) : (
          <MenuItem
            onClick={() => {
              if (selectedReport) {
                onEmailReport(selectedReport);
              }
              handleCloseMenu();
            }}
          >
            <ListItemIcon>
              <EmailIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Email Report" />
          </MenuItem>
        )}
        {selectedReport?.canGenerateInvoiceExport && (
          <MenuItem
            onClick={() => {
              onGenerateInvoiceExport(selectedReport);
              handleCloseMenu();
            }}
          >
            <ListItemIcon>
              <DownloadIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Download Invoice CSV" />
          </MenuItem>
        )}
        {selectedReport?.canSendReplacementInvoice && (
          <MenuItem
            onClick={() => {
              onSendReplacement(selectedReport);
              handleCloseMenu();
            }}
          >
            <ListItemIcon>
              <ReceiptLongIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Generate Replacement Invoice" />
          </MenuItem>
        )}
        {canDeleteReport && (
          <MenuItem
            onClick={() => {
              if (selectedReport) {
                onDeleteReport(selectedReport);
              }
              handleCloseMenu();
            }}
          >
            <ListItemIcon>
              <DeleteOutlineIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Delete Report" />
          </MenuItem>
        )}
      </Menu>
    </Dialog>
  );
}
